import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFamily } from '../contexts/FamilyContext';
import { toast } from 'sonner';
import { Users, Crown, Check, Plus, ArrowRight } from 'lucide-react';
import Logo from '../components/Logo';

const FamilyPicker = () => {
  const navigate = useNavigate();
  const { families, switchFamily, currentFamily } = useFamily();
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(currentFamily?.id || null);

  const handleSelectFamily = async (familyId) => {
    if (familyId === currentFamily?.id) {
      navigate('/dashboard');
      return;
    }

    setLoading(true);
    setSelectedId(familyId);
    try {
      const result = await switchFamily(familyId);
      toast.success(result.message);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to switch family');
      setSelectedId(currentFamily?.id);
    } finally {
      setLoading(false);
    }
  };

  const getPlanBadge = (planType) => {
    if (planType === 'premium') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
          <Crown size={12} />
          Premium
        </span>
      );
    }
    return (
      <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
        Free
      </span>
    );
  };

  const getRoleBadge = (role) => {
    if (role === 'admin') {
      return (
        <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">
          Admin
        </span>
      );
    }
    return (
      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
        Member
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Logo size="large" className="justify-center mb-4" />
          <h1 className="text-2xl font-bold text-gray-800">Select Family</h1>
          <p className="text-gray-600 mt-2">Choose which portfolio to view</p>
        </div>

        {/* Family List */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="divide-y">
            {families.map((family) => (
              <button
                key={family.id}
                onClick={() => handleSelectFamily(family.id)}
                disabled={loading}
                className={`w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors ${
                  selectedId === family.id ? 'bg-emerald-50' : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    selectedId === family.id ? 'bg-emerald-100' : 'bg-gray-100'
                  }`}>
                    <Users size={24} className={selectedId === family.id ? 'text-emerald-600' : 'text-gray-500'} />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-gray-800">{family.name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      {getPlanBadge(family.plan_type)}
                      {getRoleBadge(family.role)}
                    </div>
                  </div>
                </div>
                {selectedId === family.id && loading ? (
                  <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                ) : currentFamily?.id === family.id ? (
                  <Check size={24} className="text-emerald-600" />
                ) : (
                  <ArrowRight size={24} className="text-gray-400" />
                )}
              </button>
            ))}
          </div>

          {/* Create New Family */}
          <div className="border-t p-4">
            <button
              onClick={() => navigate('/family-setup')}
              className="w-full flex items-center justify-center gap-2 py-3 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
            >
              <Plus size={20} />
              <span className="font-medium">Create New Family</span>
            </button>
          </div>
        </div>

        {/* Continue Button */}
        {currentFamily && (
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full mt-4 btn-primary py-3"
          >
            Continue with {currentFamily.name}
          </button>
        )}
      </div>
    </div>
  );
};

export default FamilyPicker;
